/**
 * MAP (in-file navigation graph — any LLM reads this without external tooling;
 * keep in sync with the code):
 *   role:      controller — core public booking flow: category/product/
 *              contact selection, calendar availability, extra people,
 *              pricing, confirm booking, waiting list, incoming vouchers
 *   extends:   Controller (@ember/controller)
 *   template:  none
 *   qp:        category, product, contact, payment_id
 *   key:       me getter, syncMe/_initMeTask, step0..step4 getters,
 *              addWaitingListTask, checkMeAddressTask, getTotalTask,
 *              loadDataTask, updateMonthTask, confirmBookingTask,
 *              setCategory/setContact/setProduct/addPerson/delPerson/
 *              setDateHour actions
 *   uses:      beebeeboard-booking/lib/booking-filters, booking-people,
 *              booking-request, booking-calendar, booking-steps,
 *              booking-validation (local app libs), beebee-base/lib/utility,
 *              beebee-base/lib/get-config, moment-timezone, services:
 *              router/intl/notify/session/store/log, applicationController
 *   siblings:  routes/index.js, templates/index.hbs
 *   tests:     controllers-logic-test.js, money-test.js,
 *              index-booking-path-test.js, route-hooks-test.js,
 *              booking-routes-test.js, booking-regressions-test.js,
 *              wave6-coverage-test.js, wave7-coverage-test.js,
 *              wave8-coverage-test.js, wave10-coverage-test.js,
 *              wave16-coverage-test.js, wave17-coverage-test.js
 *   gotchas:   `me` is a pure render-time getter with NO side effects — the
 *              me-init side effects (meUiFlags mutation) run from the
 *              `syncMe` did-insert/did-update modifier in index.hbs instead,
 *              because firing them from the getter raised a backtracking
 *              assertion (paid-booking branch swapping session.me while the
 *              contact form is still mounted, colliding with
 *              BbInputTel's did-update read of this.me.mobile). updateMonthTask
 *              destructures `{ date, ... }` — passing a bare Date at any call
 *              site wipes dateCalendar to undefined and silently refetches
 *              the current month instead of the intended one. confirmBookingTask:
 *              any throwable after the POST is accepted (analytics, a
 *              non-JSON reply, the payment lookup) that isn't
 *              `e.responseJSON` used to leave bookError unset — a silent
 *              no-op with the button re-enabled, risking a double booking;
 *              now always sets a generic error. totalAPI can resolve to
 *              `null` (empty 200 body / no match) — reading `.acconto` on it
 *              threw (Sentry EMBER-BOOKING-V4), so getTotalTask bails early.
 *              arrWaitListDays uses a fixed [1,2,3,4,5,6,0] Monday-first
 *              mapping matching addWaitingListTask's positional flags —
 *              `moment.weekdaysShort(true)` would shift labels under
 *              Sunday-first locales.
 *
 * The main booking-portal controller: walks the operator/product/contact ->
 * calendar/slot -> people -> confirm steps, computes pricing, and posts the
 * final booking (or waiting-list request).
 */
import Controller from '@ember/controller'
import ENV from 'beebeeboard-booking/config/environment'
import moment from 'moment-timezone';

import getConfig from 'beebee-base/lib/get-config';
import { tracked } from '@glimmer/tracking';
import {
    beebeeAjaxGet,
    beebeeAjaxPost,
    beebeeAjaxPatch
} from 'beebee-base/lib/utility'

import {
    action,
    computed,
    get,
    set
} from '@ember/object'

import {
    task,
    timeout,
    didCancel
} from 'ember-concurrency'

import {
    inject as iService
} from '@ember/service'

import {
    inject as iController
} from '@ember/controller'

import {
    filterContacts,
    filterProducts,
    filterProductsByContact,
    hasNoAvailableSlots,
    isItalianAddress
} from 'beebeeboard-booking/lib/booking-filters'

import {
    blankPerson,
    buildPerson,
    meUiFlags,
    rebuildVoucherPeople
} from 'beebeeboard-booking/lib/booking-people'

import {
    buildConfirmBookingPayload,
    buildWaitingListPayload,
    trackPurchase
} from 'beebeeboard-booking/lib/booking-request'

import {
    clearCalendarAvailability,
    markCalendarAvailability,
    monthRange
} from 'beebeeboard-booking/lib/booking-calendar'

import {
    contactFieldRequirements,
    effectiveBookingSettings,
    isStep1Done,
    isStep2Done,
    peopleFieldRequirements,
    productSkipsOnlinePayment
} from 'beebeeboard-booking/lib/booking-steps'

import {
    clearRecordAttributes,
    isAddressIncomplete,
    validateContact,
    validateGdprOptions,
    validatePeople
} from 'beebeeboard-booking/lib/booking-validation'

export default class IndexController extends Controller {
    // payment_id is set by the payment-status route redirect (return URL from
    // payment gateways); registering it avoids the unknown-query-param
    // assertion and keeps the deep link intact.
    queryParams = ['category', 'product', 'contact', 'payment_id']
    payment_id = null
    @iService router
    @iService intl
    @iService notify
    @iService session
    @iService store
    @iService log
    @iController('application') applicationController

    @tracked dateCalendar = new Date()
    @tracked dateSelected = null

    hidePartecipoIo = false
    add_younger = false

    // Monday-first localized short day names, matching the positional
    // monday..sunday flag mapping in addWaitingListTask (index 0=monday ..
    // index 6=sunday). moment.weekdaysShort(true) is locale first-day-aware
    // and would shift labels by one under Sunday-first locales.
    arrWaitListDays = [1, 2, 3, 4, 5, 6, 0].map((d) => ({
      name: moment.weekdaysShort(d),
      value: false,
    }))
    // Two-way bound to a Textarea in index.hbs; needs @tracked to repaint.
    @tracked waitListNotes = null

    //hourSelected = true
    people = []

    selectedTimeZone = moment.tz.guess()
    timeZoneLists = moment.tz.names()

    get loadDataObj() {
      this.loadDataTask.perform();
      return '';
    }

    get requireLoginForBooking() {
      const requireLoginForBooking = this.session.option_field?.booking_settings?.require_login_for_booking || false;
      return requireLoginForBooking && !this.session.me?.id;
    }

    @computed('slots.slots.@each.busy')
    get hasNoAvailableSlots() {
      return hasNoAvailableSlots(get(this, 'slots.slots'));
    }

    // booking_settings narrowed by the selected product: a product paid on
    // site (option_json.skip_online_payment) never requires an online payment.
    @computed('session.option_field.booking_settings', 'productSelected.option').readOnly()
    get bookingSettingsEffective() {
      return effectiveBookingSettings(
        this.session.option_field?.booking_settings,
        productSkipsOnlinePayment(this.productSelected)
      );
    }

    // Effective payment gate read by the templates.
    get paymentRequired() {
      return this.bookingSettingsEffective.payment_required;
    }

    @computed('session.option_field.booking_settings.field_required', 'bookingSettingsEffective').readOnly()
    get fieldRequired() {
      return contactFieldRequirements(this.bookingSettingsEffective);
    }

    @computed('session.option_field.booking_settings.field_required').readOnly()
    get fieldPeopleRequired() {
      return peopleFieldRequirements(this.session.option_field.booking_settings);
    }

    get notRequirePassword() {
      return this.session.option_field?.booking_settings?.not_require_password;
    }

    get showCreaAccount() {
      const meId = this.me?.id;
      const voucherToUse = this.applicationController.voucherToUse;

      return !voucherToUse && !meId && !this.notRequirePassword;
    }

    @computed('session.option_field.booking_settings.operatore_first').readOnly()
    get operatoreFirst() {
      const operatoreFirst = get(this, 'session.option_field.booking_settings.operatore_first');

      return operatoreFirst || false;
    }

    @computed('dateCalendar')
    get currentMonth() {
      return moment(this.dateCalendar).format('MMMM');
    }

    @computed('dateCalendar')
    get isNotCurrentMonth() {
      const currentDate = moment();
      const selectedDate = moment(this.dateCalendar);

      return !selectedDate.isSame(currentDate, 'month');
    }

    _emptyContact = null;
    _lastMeId = null;

    // Pure getter: NO side effects. It is read during render (directly and via
    // `isItaliano`, `showCreaAccount`, …). The me-init side effects are driven
    // from a render modifier — see `syncMe` — because firing `_initMeTask` here
    // let its `me.setProperties(meUiFlags(me))` mutate `mobile` in the same
    // computation in which `<BbInputTel>`'s `{{did-update this.onValueChange
    // @value}}` had already read `this.me.mobile`, raising a backtracking
    // assertion when the paid-booking branch swaps `session.me` while the
    // contact form is still mounted.
    get me() {
      let me = this.session.me;

      if (!me?.id) {
        if (!this._emptyContact || this._emptyContact.isDestroyed || this._emptyContact.isDestroying) {
          this._emptyContact = this.store.createRecord('contact');
          this.checkMeAddressTask.perform(this._emptyContact);
        }
        return this._emptyContact;
      }

      return me;
    }

    // Run the UI-flag init only when the contact changes, from a
    // did-insert/did-update modifier on `this.me.id` (see index.hbs) so the
    // mutation happens AFTER render, never inside the render that read `me`.
    @action
    syncMe() {
      const me = this.session.me;

      if (!me?.id) {
        this._lastMeId = null;
        return;
      }

      if (this._lastMeId !== me.id) {
        this._lastMeId = me.id;
        this._initMeTask.perform(me);
      }
    }

    @task({ restartable: true })
    *_initMeTask(me) {
      const { first_name: firstName, last_name: lastName, fisica_giuridica } = me;

      if (!fisica_giuridica && (firstName || lastName)) {
        me.set('fisica_giuridica', false);
      }

      me.setProperties(meUiFlags(me));

      this.checkMeAddressTask.perform(me);
    }

    get isItaliano() {
      return isItalianAddress(this.me?.addresses?.[0]);
    }

    @(task(function* (ediItem = null) {
      // `{{on ... (perform this.addWaitingListTask)}}` appends the DOM Event — strip it.
      if (ediItem instanceof Event) {
        ediItem = null;
      }
      const {
        me,
        productSelected,
        contactSelected,
        arrWaitListDays,
        waitListNotes,
        session,
        intl,
        applicationController
      } = this;

      const url = `${applicationController.urlApi}/waitinglist${ediItem?.id ? `/${ediItem.id}` : ''}`;

      const data = buildWaitingListPayload({
        me,
        productSelected,
        contactSelected,
        arrWaitListDays,
        waitListNotes
      });

      // Invio la richiesta
      try {
        if (ediItem?.id) {
          yield beebeeAjaxPatch(url, session.token, data);
        } else {
          yield beebeeAjaxPost(url, session.token, data);
        }
      } catch (e) {
        this.log.warn('[addWaitingListTask] request failed:', e?.status, e?.statusText);
        this.notify.error(intl.t('save error'));
        return;
      }

      this.notify.success(intl.t('save done'));
      set(this, 'waitListNotes', null);
      set(this, 'openWaitList', null);

      this.loadDataTask.perform();

    }).restartable()) addWaitingListTask

    @(task(function*(me) {
      yield timeout(250)

      if (get(me, 'addresses') && !get(me, 'addresses.length')) {
        const address = this.store.createRecord('address', {
          'is_invoice': true,
          'location_address_country': 'Italia',
          'location_address_country_code': 'IT'
        });
        get(me, 'addresses').push(address);
      }
    }).restartable()) checkMeAddressTask;

    @computed('contact', 'dispos.contacts.[]').readOnly()
    get contactSelected() {
      const dispos = this.dispos;
      const contact = parseInt(this.contact);

      if (dispos && Array.isArray(dispos.contacts)) {
        return dispos.contacts.find(i => i.id === contact) || null;
      }
      return null;
    }

    @computed('product', 'dispos.products.[]').readOnly()
    get productSelected() {
      const dispos = this.dispos;
      const product = parseInt(this.product);

      if (dispos && Array.isArray(dispos.products)) {
        return dispos.products.find(i => i.id === product) || null;
      }
      return null;
    }

    @computed('category', 'dispos.product_categories.[]').readOnly()
    get categorySelected() {
      const dispos = this.dispos;
      const category = parseInt(this.category);

      if (dispos && Array.isArray(dispos.product_categories)) {
        return dispos.product_categories.find(i => i.id === category) || null;
      }
      return null;
    }

    @computed('dispos.product_categories.length').readOnly()
    get skipStep0() {
      const productCategoriesLength = get(this, 'dispos.product_categories.length');

      return !productCategoriesLength;
    }

    @computed('operatoreFirst').readOnly()
    get skipStep1() {
      const operatoreFirst = this.operatoreFirst;
      const skipSelectProduct = get(this, 'session.option_field.booking_settings.skip_product') || false;

      return operatoreFirst ? skipSelectProduct : false;
    }

    @computed('operatoreFirst').readOnly()
    get skipStep2() {
      const operatoreFirst = this.operatoreFirst;
      const skipSelectContact = get(this, 'session.option_field.booking_settings.skip_contact') || false;

      return operatoreFirst ? false : skipSelectContact;
    }

    @computed('skipStep0', 'categorySelected.id').readOnly()
    get step0() {
      return this.skipStep0 || !!this.categorySelected;
    }

    @computed('operatoreFirst', 'productSelected.id', 'contactSelected.id', 'step0').readOnly()
    get step1() {
      return isStep1Done({
        contactSelected: this.contactSelected,
        productSelected: this.productSelected,
        operatoreFirst: this.operatoreFirst,
        step0: this.step0,
        skipStep0: this.skipStep0,
        skipContact: this.session.option_field?.booking_settings?.skip_contact || false,
        skipProduct: this.session.option_field?.booking_settings?.skip_product || false
      });
    }

    @computed('operatoreFirst', 'contactSelected.id', 'productSelected.id', 'step1').readOnly()
    get step2() {
      return isStep2Done({
        contactSelected: this.contactSelected,
        productSelected: this.productSelected,
        operatoreFirst: this.operatoreFirst,
        step1: this.step1,
        skipContact: this.session.option_field?.booking_settings?.skip_contact || false,
        skipProduct: this.session.option_field?.booking_settings?.skip_product || false
      });
    }

    @computed('step2', 'dateSelected', 'hourSelected').readOnly()
    get step3() {
      if (!this.step2) {
        return false;
      }

      return !!this.dateSelected && !!this.hourSelected;
    }

    @computed('step3', 'applicationController.voucherToBy.gift').readOnly()
    get step4() {
      return this.step3 || this.applicationController.voucherToBy?.gift;
    }

    @computed('applicationController.voucherToBy.gift').readOnly()
    get voucherToBuy() {
      if(!this.applicationController.voucherToBy?.gift) {
        return null;
      }

      this.getTotalTask.perform();

      return this.applicationController.voucherToBy.gift;
    }

    @computed('step0').readOnly()
    get step0Running() {
        return !this.step0
    }

    @computed('step0', 'step1', 'skipStep0').readOnly()
    get step1Running() {
      return !this.step1 && (this.step0 || this.skipStep0);
    }

    @computed('step1', 'step2').readOnly()
    get step2Running() {
      return this.step1 && !this.step2;
    }

    @computed('step2', 'step3').readOnly()
    get step3Running() {
      if (this.step2 && !this.step3) {
        if (!this.dispos_slot) {
          // updateMonthTask destructures `{ date, … }`: passing a bare Date made
          // `date` undefined and its first statement (`this.dateCalendar = date`)
          // wiped the calendar's centre on the way into the day picker.
          get(this, 'updateMonthTask').perform({
            date: this.dateCalendar ? this.dateCalendar : new Date()
          });
        }
        set(this, 'bookConfirmed', false);
        return true;
      }
      return false;
    }

    @computed('productSelected.id', 'dispos.contacts.[]', 'categorySelected.id').readOnly()
    get contactsFiltered() {
      return filterContacts(this.dispos, this.categorySelected, this.productSelected);
    }

    @computed('contactSelected.id', 'dispos.[]', 'categorySelected.id').readOnly()
    get productsFiltered() {
      return filterProducts(this.dispos, this.categorySelected, this.contactSelected);
    }

    filterProductsByContact(products) {
      return filterProductsByContact(products, this.contactSelected);
    }

    @action
    setCategory(c, e) {
        set(this, 'category', c.id)
        this.scrollTop()
    }

    @action
    setContact(c, e) {
        if(this.operatoreFirst) {
            if (Array.isArray(this.dispos.products) && this.dispos.products.length === 1) {
                set(this, 'product', this.dispos.products[0].id);
            } else {
                set(this, 'product', null);
            }
        }

        set(this, 'contact', (this.contactSelected && this.contactSelected.id === c.id) ? null : c.id)

        this.scrollTop()
    }

    @action
    setProduct(p, e) {
        if(!this.operatoreFirst) {
            if (Array.isArray(this.dispos.contacts) && this.dispos.contacts.length === 1) {
                set(this, 'contact', this.dispos.contacts[0].id);
            } else {
                set(this, 'contact', null);
            }
        }

        set(this, 'product', (this.productSelected && this.productSelected.id === p.id) ? null : p.id)

        this.scrollTop()
    }

    @action addOnlyPerson(add_younger = false) {

        set(this, 'add_younger', add_younger)

        if (!add_younger || !Array.isArray(this.people)) {
            set(this, 'people', [])
        }

        if(add_younger) {
            set(this, 'people', [...(this.people || []), blankPerson()])
        }
    }

    @action addPerson(me) {
      const showPeople = this.session?.option_field?.booking_settings?.show_people;
      const hidePeople = this.session?.option_field?.booking_settings?.hide_people;

      if (!showPeople || hidePeople) {
        return;
      }

      const p = buildPerson(me);

      if (me) {
        set(this, 'hidePartecipoIo', true);
      }

      if (!Array.isArray(this.people) || (me && this.people.length === 1)) {
        set(this, 'people', []);
      }

      set(this, 'people', [...(this.people || []), p]);
      this.getTotalTask.perform();
    }

    @computed('this.hourSelected.rimanenti', 'people.length')
    get canAddPerson() {
        if (this.hourSelected && this.hourSelected.rimanenti && this.people.length >= this.hourSelected.rimanenti) {
            return false
        }

        return true
    }

    /*@computed('productSelected.prezzo_a_persona', 'people.length')
    get totalPrice() {
        if (this.productSelected?.prezzo_a_persona) {
            return this.productSelected.price * this.people.length
        }

        return this.productSelected?.price || null
    }*/

    @action delPerson(person) {
        if (Array.isArray(this.people)) {
            set(this, 'people', this.people.filter((p) => p !== person))
            set(this, 'hidePartecipoIo', false)
            this.getTotalTask.perform()
        }
    }

    @action setMeMobile(value) {
        set(this, 'me.mobile', value);
    }

    @action setMeIsMobileValid(value) {
        set(this, 'me.isMobileValid', value);
    }

    @action setMeFisicaGiuridica(value) {
        set(this, 'me.fisica_giuridica', value);
    }

    @action setMeDataNascita(value) {
        set(this, 'me.data_nascita', value);
    }

    @(task(function*() {
        yield timeout(250);
        set(this, 'totalAPI', null);

        const { start, end, stanza } = this.hourSelected || {};
        const { id: product_id, prezzo_a_persona } = this.productSelected || {};
        const { voucherToBuy, people } = this;
        const is_voucher = !!voucherToBuy;
        const start_date = voucherToBuy ? null : moment(start).format('YYYY-MM-DDTHH:mmZ');
        const end_date = voucherToBuy ? null : moment(end).format('YYYY-MM-DDTHH:mmZ');

        if (!start && !voucherToBuy) return;

        try {
          const totalAPI = yield beebeeAjaxPost(
          `${getConfig(this).APP.urlApi}/booking_calcola_prezzo`,
          null,
          {
              is_voucher,
              voucher: voucherToBuy,
              product_id,
              start_date,
              end_date,
              stanza,
              prezzo_a_persona,
              people: voucherToBuy ? voucherToBuy.people_number : (Array.isArray(people) ? people.length || 1 : 1)
            });

          // The price endpoint can resolve to null (empty 200 body / no match);
          // reading `.acconto` on it threw "null is not an object" (Sentry
          // EMBER-BOOKING-V4). Nothing to show without a total — bail.
          if (!totalAPI) {
            set(this, 'totalAPI', null);
            return;
          }
          // acconto sentinels from booking_calcola_prezzo_v1:
          //   -1 = no deposit configured (the full price is due online)
          //   -2 = product paid on site, no online payment at all
          // The product flag is also read client-side so the summary is right
          // even against an API that does not send the key yet.
          totalAPI.skip_online_payment =
            totalAPI.skip_online_payment === true ||
            totalAPI.acconto === -2 ||
            productSkipsOnlinePayment(this.productSelected);
          totalAPI.box_acconto = totalAPI.acconto !== -1 && !totalAPI.skip_online_payment;
          set(this, 'totalAPI', totalAPI);
        } catch (error) {
          this.log.error('index: totalAPI acconto compute failed', error);
        }
    }).restartable()) getTotalTask

    @(task(function*() {
      const optionField = this.session.option_field;
      const requireLoginForBooking = optionField?.booking_settings?.require_login_for_booking || false;
      if (!this.session.isAuthenticated() && (requireLoginForBooking || !optionField)) {
        return;
      }

      try {
        const dispos = yield beebeeAjaxGet(`${getConfig(this).APP.urlApi}/dispos_data${requireLoginForBooking ? '_user' : ''}`,
          this.session.token
        );

        if (dispos) {
          const { contacts, products, product_categories } = dispos;

          if (Array.isArray(contacts) && contacts.length === 1 && !this.contact) {
            set(this, 'contact', contacts[0].id);
          }

          if (Array.isArray(products) && products.length === 1 && !this.product) {
            set(this, 'product', products[0].id);
          }

          if (Array.isArray(product_categories) && product_categories.length === 1 && !this.category) {
            set(this, 'category', product_categories[0].id);
          }

          set(this, 'dispos', dispos);

          if (this.step3Running) {
            // Same `{ date }` shape as every other call site: a bare Date here
            // reset dateCalendar to undefined and refetched the CURRENT month,
            // discarding whatever month the user had navigated to.
            get(this, 'updateMonthTask').perform({ date: this.dateCalendar || new Date() });
          }
        }
      } catch (error) {
        this.log.error('index: step3 updateMonth failed', error);
      }
    }).restartable()) loadDataTask

    @(task(function*({
        date,
        rec_number,
        stop_search
    }) {
        this.dateCalendar = date;
        clearCalendarAvailability();

        yield timeout(250);
        const { from_date, to_date } = monthRange(date, this.selectedTimeZone);

        try {
          const requireLoginForBooking = this.session.option_field?.booking_settings?.require_login_for_booking || false;
          const dispos = yield beebeeAjaxGet(
          `${getConfig(this).APP.urlApi}/dispos${this.productSelected?.is_group ? '_groups' : ''}${requireLoginForBooking ? '_user' : ''}`,
            this.session.token,
          {
              contact_id: this.contactSelected?.id || null,
              product_id: this.productSelected?.id || null,
              from_date,
              to_date
            });

          let noDispoAvailable = true;

          if (Array.isArray(dispos?.giorni_array)) {
            noDispoAvailable = false;
            markCalendarAvailability(dispos.giorni_array);
          } else if (!stop_search && (!rec_number || rec_number < 3)) {
            let dateN = this.dateCalendar || new Date();
            yield this.updateMonthTask.perform({
              date: new Date(dateN.getFullYear(), dateN.getMonth() + 1, 1),
              rec_number: rec_number ? ++rec_number : 1
            });
          }

          set(this, 'noDispoAvailable', noDispoAvailable);
          set(this, 'dispos_slot', dispos);
        } catch (error) {
          this.log.error('index: dispo slot compute failed', error);
        }
    }).restartable()) updateMonthTask

    @(task(function*() {
        set(this, 'bookError', null)
        set(this, 'requiredEmpty', {})

        const voucherToUse = this.applicationController.voucherToUse
        // Effective settings: a product paid on site must not demand the
        // invoicing fields (cod_fiscale, piva, codice_destinatario, address).
        const bookingSettings = this.bookingSettingsEffective

        let emptyRequiredField = validateGdprOptions(
            this.applicationController.gdprField.organization_term_options
        )

        let me = this.me;

        if (me.id) {
            me = get(this, 'store').peekRecord('contact', me.id)
        }

        clearRecordAttributes(me)

        const contactCheck = validateContact({
            me,
            fieldRequired: this.fieldRequired,
            bookingSettings,
            voucherToUse,
            usaPec: this.usa_pec,
            isItaliano: get(this, 'isItaliano'),
            notRequirePassword: get(this, 'session.option_field.booking_settings.not_require_password')
        })

        const requiredEmpty = contactCheck.requiredEmpty
        emptyRequiredField = emptyRequiredField || contactCheck.emptyRequiredField

        if (!voucherToUse && this.fieldRequired.addresses) {
          const addresses = (yield get(me, 'addresses'))[0] || [];
            if (!voucherToUse && isAddressIncomplete(addresses)) {
                requiredEmpty.addresses = true
                emptyRequiredField = true
            }
        }

        const peopleCheck = validatePeople({
            people: this.people,
            fieldPeopleRequired: this.fieldPeopleRequired,
            productSelected: this.productSelected,
            intl: this.intl,
            bookingSettings: {
                hide_people: get(this, 'session.option_field.booking_settings.hide_people'),
                show_people: get(this, 'session.option_field.booking_settings.show_people')
            }
        })

        if (peopleCheck.peopleMissing) {
            requiredEmpty.people = true
        }

        emptyRequiredField = emptyRequiredField || peopleCheck.emptyRequiredField
        const bookErrorArr = peopleCheck.bookErrorArr

        set(this, 'requiredEmpty', requiredEmpty)

        if (emptyRequiredField) {
            set(this, 'bookError', {
                'errors': [{
                        'detail': this.intl.t('empty required field')
                    },
                    ...bookErrorArr
                ]
            })
            this.scrollTop()
            return
        }

        const peopleForRequest = Array.isArray(this.people) ? [...this.people] : [];

        if(this.applicationController.voucherToUse) {
          peopleForRequest.push({
            'id': this.me.id,
            'first_name': this.me.first_name,
            'last_name': this.me.last_name,
            'mobile':  this.me.mobile,
            'data_nascita': this.me.data_nascita,
            'same': true
          });
        }

        const url = voucherToUse ? 'booking_use_voucher' : 'confirm_booking';
        const confirmData = buildConfirmBookingPayload({
            me: this.me,
            people: peopleForRequest,
            voucher: this.applicationController.voucherToBy || voucherToUse,
            voucherToBuy: this.voucherToBuy,
            contactSelected: this.contactSelected,
            productSelected: this.productSelected,
            hourSelected: this.hourSelected,
            selectedTimeZone: this.selectedTimeZone,
            organizationTermOptions: this.applicationController.gdprField.organization_term_options,
            organizationName: get(this, 'session.openOrganization.name'),
            thumbnailUrl: get(this, 'session.openOrganization.thumbnail_url'),
            primaryColor: get(this, 'session.openOrganization.primary_color'),
            userId: this.notRequirePassword ? null : get(this, 'session.me.id'),
            lang: this.intl.primaryLocale
        });

        try {
            let confirm = yield beebeeAjaxPost(
          `${getConfig(this).APP.urlApi}/${url}`,
          null,
          confirmData);

            // Prenotazione riuscita: pulisci lo stato che si "portava dietro" nella
            // prenotazione successiva della stessa sessione.
            //  - `dispos_slot`: `step3Running` ricarica gli slot SOLO se è vuoto,
            //    quindi senza azzerarlo la 2ª visita vedeva la dispo STALE (lo slot
            //    appena occupato risultava libero) e falliva al confirm.
            //  - `people`: i partecipanti extra restavano e finivano anche nella
            //    prenotazione successiva (setPeople li re-inizializza al rientro).
            set(this, 'dispos_slot', null);
            clearCalendarAvailability();
            set(this, 'people', []);

            if (confirm.id === 0 || confirm.id === '0') {
                trackPurchase(this.productSelected);

                if (confirm.redirect) {
                  window.location.replace(confirm.redirect);
                } else {
                  set(this, 'bookConfirmed', true)
                }
            } else {
                this.session.authenticate(confirm.token)
                yield timeout(50)
                yield this.session.reloadMeTask.perform();

                let payment = yield get(this, 'store').findRecord('payment', confirm.id, {
                    'reload': true
                });

                set(this, 'request_by', confirm.payment_user_id)
                set(this, 'payment', payment)
                set(this, 'showPayment', true)
            }
        } catch (e) {
            const errors = e.responseJSON?.errors;
            const firstTitle = errors?.[0]?.title;

            if (firstTitle === 'Utente già esistente') {
                this.notify.error('Utente già esistente. Accedi per completare la prenotazione.');
            } else if (e.responseJSON) {
                set(this, 'bookError', e.responseJSON)
            } else {
                // Not an HTTP error, or one without a body: `e.responseJSON` is
                // undefined and assigning it left `bookError` empty, so the
                // template rendered NOTHING. The whole success path runs inside
                // this try, so any throwable after an accepted POST (analytics,
                // a non-JSON reply, the payment lookup) produced a silent no-op:
                // unchanged form, no message, button re-enabled — and the user
                // presses it again, booking twice. Always say something.
                this.log.error('index: confirmBooking failed', e);
                set(this, 'bookError', {
                    errors: [{ detail: this.intl.t('comunication error') }]
                })
            }
        }
    }).drop()) confirmBookingTask;

    @action
    handleCenterChange(arg) {
        this.updateMonthTask.perform(arg).catch((e) => {
            if (!didCancel(e)) throw e;
        });
    }

    @action
    nextMonth() {
        const date = this.dateCalendar || new Date();

        this.updateMonthTask.perform({
            date: new Date(date.setMonth(date.getMonth() + 1))
        });
    }

    @action
    goCurrentMonth() {
        this.updateMonthTask.perform({
            'date': new Date(),
            'stop_search': true
        })
    }

    @action
    updateDaySelected(selected) {
        // Replaces {{action 'updateDay' value='date'}}: extract .date from the calendar selection
        this.updateDay(selected.date);
    }

    @action
    updateDay(date) {
        this.dateSelected = date;

        const disposSlot = get(this, 'dispos_slot.giorni');
        const formattedDate = moment(date).tz(this.selectedTimeZone).format('YYYY-MM-DD');
        const slots = disposSlot ? disposSlot[formattedDate] : null;

        if (slots) {
            // Real optional chaining: Ember get() would treat the '?' as part of
            // the key and always return undefined, disabling this filter.
            const minPeopleNumber = this.applicationController.voucherToUse?.voucher?.option?.gift?.people_number;
            if (minPeopleNumber) {
                slots.slots = slots.slots.filter(slot => !slot.rimanenti || slot.rimanenti >= minPeopleNumber);
            }
        }

        set(this, 'slots', slots);
    }

    @action
    confirmBooking(event) {
        get(this, 'confirmBookingTask').perform()
        event.preventDefault()
    }

    @action
    setDateHour(hour) {
        // Busy slots are not selectable: no state change at all.
        if (hour.busy) {
          return;
        }

        set(this, 'hourSelected', hour);
        // Advancing to step 4: reveal the "partecipo io" option (was previously
        // toggled as a side-effect inside the step4 getter).
        set(this, 'hidePartecipoIo', false);

        this.scrollTop();

        if (this.me && get(this, 'session.option_field.booking_settings.show_people')) {
          this.addPerson(this.me);
        }

        if (this.applicationController.voucherToUse?.voucher?.option?.gift?.people_number) {
          const nP = this.applicationController.voucherToUse.voucher.option.gift.people_number;

          set(this, 'people', rebuildVoucherPeople(this.people, nP));
        }

        if (!this.contact && hour.contact_id) {
          set(this, 'contact', hour.contact_id);
        }

        this.getTotalTask.perform();
    }

    @action
    clickStep0() {
        set(this, 'category', null)
        set(this, 'contact', null)
        set(this, 'product', null)
        this.baseReset()
    }

    @action
    clickStep1() {
        if(get(this, 'session.option_field.booking_settings.step1_disable')) {
            return;
        }

         set(this, 'contact', null)
        set(this, 'product', null)
        this.baseReset()
    }

    @action
    clickStep2() {
      const applicationController = get(this, 'applicationController');

      const bookingSettings = this.session.option_field?.booking_settings || {
        step2_disable: null,
        skip_product: null,
        skip_contact: null,
        step1_disable: null
      };

      const {
        step2_disable,
        skip_product,
        skip_contact,
        step1_disable
      } = bookingSettings;

      const isOperatorFirst = this.operatoreFirst;

      if (step2_disable || (isOperatorFirst && skip_product && step1_disable) || (!isOperatorFirst && skip_contact && step1_disable)) {
        return;
      }

      const propertyToSet = isOperatorFirst ? (skip_product ? 'contact' : 'product') : (skip_contact ? 'product' : 'contact');

      set(this, propertyToSet, null);

      this.baseReset();
    }

    @action
    clickStep3() {
        this.baseReset();
        if(this.voucherToBuy) {
          this.router.transitionTo('vouchers');
        }
    }

    @action
    backCalendar() {
        set(this, 'contact', null)
        set(this, 'product', null)
        this.baseReset()
    }

    scrollTop() {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    baseReset() {
        set(this, 'dateSelected', null)
        set(this, 'hourSelected', null)
        // Stepping back out of step 4: hide the "partecipo io" option (was
        // previously toggled as a side-effect inside the step4 getter).
        set(this, 'hidePartecipoIo', true)
        set(this, 'dispos_slot', null)
        set(this, 'showPayment', null)
        set(this, 'bookError', null)
    }

    destroyRecord(record, redirect_url, notifyUser = true) {
        var self = this,
            id = get(record, 'id')

        if (!record || get(record, 'currentState.stateName') === 'root.deleted.saved') {
            set(self, 'operationDestroy', id)
            return new Promise(function(resolve /*, reject*/ ) {
                resolve(record)
            })

        } else if (!get(record, 'id')) {
            set(self, 'operationDestroy', id)
            return record.deleteRecord()
        }

        return record.destroyRecord().then(function() {
            return true
        }, function(reason) {
            return false
        })
    }
}
